/**
 * Drawer de detalhes/logs de um onboarding job ZTNA.
 *
 * Busca o job completo via GET /jobs/{id} (a tabela de histórico usa um DTO
 * mais leve). Timeline amigável (label + descrição + DURAÇÃO por etapa), log ao
 * vivo (SSE, para jobs ativos), erro resumido + "Avançado" recolhido (JSON), e
 * resumo no sucesso. Dark-mode via tokens do tema.
 */
import { useSyncExternalStore } from 'react'
import { useSeguirOFim } from '@/hooks/useSeguirOFim'
import {
  Alert,
  Badge,
  Code,
  Divider,
  Drawer,
  Group,
  Loader,
  ScrollArea,
  Spoiler,
  Stack,
  Text,
  Timeline,
} from '@mantine/core'
import {
  IconAlertTriangle,
  IconCheck,
  IconCircle,
  IconX,
} from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { ztnaApi } from '../api'
import type { CompletedStep, JobResponse, JobStatus } from '../types'
import { ztnaJobLogStore } from '../ztnaJobLogStore'

/** Passos canônicos do pipeline (worker.run_pipeline) com rótulo/descrição PT-BR. */
const PIPELINE_STEPS: { key: string; label: string; desc: string }[] = [
  { key: 'acm_request', label: 'Certificado TLS', desc: 'Solicitado à AWS (ACM)' },
  { key: 'acm_validation_dns', label: 'Validação de domínio', desc: 'Registros DNS de validação criados' },
  { key: 'acm_validated', label: 'Certificado emitido', desc: 'ISSUED pela AWS' },
  { key: 'route53_a_record', label: 'DNS publicado', desc: 'Registro apontando para o broker (Route53)' },
  { key: 'alb_listener_rule', label: 'Roteamento', desc: 'Regra de host no balanceador (ALB)' },
  { key: 'db_insert', label: 'Aplicação registrada', desc: 'App publicada no broker' },
  { key: 'smoke_test', label: 'Teste de fumaça', desc: 'Broker respondeu com saúde OK' },
]

function statusColor(s: JobStatus): string {
  if (s === 'success') return 'green'
  if (s === 'failed' || s === 'cancelled') return 'red'
  if (s === 'needs_validation') return 'yellow'
  return 'blue'
}

type StepState = 'done' | 'in_flight' | 'failed' | 'pending'

function stepState(
  key: string,
  completed: Map<string, CompletedStep>,
  inFlight: string | null,
  jobStatus: JobStatus,
): StepState {
  if (completed.has(key)) return 'done'
  if (inFlight === key) return jobStatus === 'failed' ? 'failed' : 'in_flight'
  return 'pending'
}

function stepIcon(state: StepState) {
  if (state === 'done') return <IconCheck size={12} />
  if (state === 'failed') return <IconX size={12} />
  // `Loader` do Mantine, não `IconLoader` do tabler: aquele é um SVG ESTÁTICO —
  // um desenho de spinner que nunca gira. O passo em andamento ficava parado
  // (TASK-9817). `color` explícito porque o bullet do passo em execução ainda
  // não está preenchido (ele vem depois do `active` da Timeline), então um
  // loader branco sumiria no fundo.
  if (state === 'in_flight') return <Loader size={12} color="blue" />
  return <IconCircle size={12} />
}

function stepColor(state: StepState): string {
  if (state === 'done') return 'green'
  if (state === 'failed') return 'red'
  if (state === 'in_flight') return 'blue'
  return 'gray'
}

function fmtSecs(secs: number): string {
  if (secs < 1) return '<1s'
  if (secs < 60) return `${Math.round(secs)}s`
  return `${Math.floor(secs / 60)}m ${(Math.round(secs) % 60).toString().padStart(2, '0')}s`
}

function durationLabel(job: JobResponse): string {
  if (!job.finished_at) return '—'
  return fmtSecs(dayjs(job.finished_at).diff(dayjs(job.created_at), 'second'))
}

/** Duração de cada etapa concluída = completed_at menos o anterior (1ª ← started/created). */
function stepDurations(job: JobResponse): Map<string, number> {
  const out = new Map<string, number>()
  const ordered = [...job.completed_steps].sort((a, b) =>
    dayjs(a.completed_at).valueOf() - dayjs(b.completed_at).valueOf(),
  )
  let prev = dayjs(job.started_at ?? job.created_at)
  for (const c of ordered) {
    const t = dayjs(c.completed_at)
    out.set(c.step, Math.max(0, t.diff(prev, 'second')))
    prev = t
  }
  return out
}

function payloadDigest(payload: Record<string, unknown>): Record<string, unknown> {
  const keys = [
    'public_hostname', 'zone_id', 'environment', 'routing_mode',
    'identity_mode', 'upstream_url', 'dns_migration',
  ]
  const out: Record<string, unknown> = {}
  for (const k of keys) if (payload[k] !== undefined) out[k] = payload[k]
  return out
}

interface Props {
  jobId: number | null
  opened: boolean
  onClose: () => void
}

export function JobDetailDrawer({ jobId, opened, onClose }: Props) {
  const { data: job, isLoading, isError } = useQuery({
    queryKey: ['ztna', 'job', jobId],
    queryFn: () => ztnaApi.getJob(jobId as number),
    enabled: opened && jobId != null,
    refetchInterval: (q) => {
      const s = (q.state.data as JobResponse | undefined)?.status
      return s === 'running' || s === 'pending' || s === 'cancelling' ? 4000 : false
    },
  })

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="lg"
      title={<Text fw={700}>Detalhes do job {jobId != null ? `#${jobId}` : ''}</Text>}
    >
      {isLoading ? (
        <Group justify="center" py="xl">
          <Loader />
        </Group>
      ) : isError || !job ? (
        <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
          Não foi possível carregar o detalhe do job.
        </Alert>
      ) : (
        <JobDetailBody job={job} />
      )}
    </Drawer>
  )
}

function JobDetailBody({ job }: { job: JobResponse }) {
  const completed = new Map<string, CompletedStep>(job.completed_steps.map((c) => [c.step, c]))
  const durations = stepDurations(job)
  const payload = job.payload as Record<string, unknown>
  const slug = (payload.slug as string) || `job-${job.id}`
  const smoke = job.smoke_attempts
  const active = job.status === 'running' || job.status === 'pending' || job.status === 'cancelling'

  const liveLog = useSyncExternalStore(ztnaJobLogStore.subscribe, () => ztnaJobLogStore.get(job.id))
  // Linha nova rola o log para o fim, como num terminal (consoleai#25060).
  const seguirOFim = useSeguirOFim<HTMLDivElement>(liveLog)

  return (
    <Stack gap="md">
      <Group gap="xs">
        <Badge color={statusColor(job.status)} variant="light">
          {job.status.toUpperCase()}
        </Badge>
        <Text fw={600}>{slug}</Text>
        <Text size="sm" c="dimmed">
          #{job.tenant_id}
          {payload.environment ? ` (${String(payload.environment)})` : ''}
        </Text>
      </Group>
      <Group gap="lg">
        <Text size="xs" c="dimmed">Início: {dayjs(job.created_at).format('YYYY-MM-DD HH:mm:ss')}</Text>
        <Text size="xs" c="dimmed">Duração: {durationLabel(job)}</Text>
        <Text size="xs" c="dimmed">Tentativa: {job.attempt_seq}</Text>
      </Group>

      <Divider label="Etapas" labelPosition="left" />
      <Timeline active={job.completed_steps.length - 1} bulletSize={22} lineWidth={2}>
        {PIPELINE_STEPS.map((s) => {
          const st = stepState(s.key, completed, job.in_flight_step, job.status)
          const dur = durations.get(s.key)
          return (
            <Timeline.Item
              key={s.key}
              bullet={stepIcon(st)}
              color={stepColor(st)}
              title={
                <Group justify="space-between" wrap="nowrap">
                  <Text size="sm" fw={st === 'pending' ? 400 : 600}>{s.label}</Text>
                  {dur != null ? (
                    <Text size="xs" c="dimmed" ff="monospace">{fmtSecs(dur)}</Text>
                  ) : null}
                </Group>
              }
            >
              <Text size="xs" c={st === 'failed' ? 'red' : st === 'in_flight' ? 'blue' : 'dimmed'}>
                {st === 'in_flight' ? 'em execução…' : st === 'failed' ? 'falhou aqui' : st === 'pending' ? 'pendente' : s.desc}
              </Text>
            </Timeline.Item>
          )
        })}
      </Timeline>

      {active && liveLog.length > 0 ? (
        <>
          <Divider label="Log ao vivo" labelPosition="left" />
          <ScrollArea.Autosize mah={140} viewportRef={seguirOFim}>
            <Stack gap={2}>
              {liveLog.slice(-12).map((ln, i) => (
                <Text key={`${ln.ts}-${i}`} size="xs" ff="monospace" c={ln.phase === 'started' ? 'dimmed' : 'teal'}>
                  {fmtSecs(ln.elapsed_seconds)} {ln.phase === 'started' ? '→' : '✓'} {ln.message}
                </Text>
              ))}
            </Stack>
          </ScrollArea.Autosize>
        </>
      ) : null}

      {job.status === 'success' ? (
        <>
          <Divider label="Resumo" labelPosition="left" />
          <Stack gap={4}>
            {payload.public_hostname ? (
              <SummaryRow k="Hostname" v={String(payload.public_hostname)} />
            ) : null}
            <SummaryRow k="Certificado" v="host exato · ISSUED" />
            {payload.upstream_url ? <SummaryRow k="Upstream" v={String(payload.upstream_url)} /> : null}
          </Stack>
        </>
      ) : null}

      {job.error ? (
        <>
          <Divider label="Erro" labelPosition="left" />
          <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
            <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{job.error}</Text>
          </Alert>
        </>
      ) : null}

      <Divider label="Avançado" labelPosition="left" />
      <Spoiler maxHeight={0} showLabel="Mostrar dados técnicos" hideLabel="Ocultar">
        <Stack gap="sm" pt="sm">
          {job.error_details && Object.keys(job.error_details).length > 0 ? (
            <div>
              <Text size="xs" c="dimmed" mb={4}>error_details</Text>
              <ScrollArea.Autosize mah={200}>
                <Code block>{JSON.stringify(job.error_details, null, 2)}</Code>
              </ScrollArea.Autosize>
            </div>
          ) : null}
          {smoke != null &&
          (Array.isArray(smoke) ? smoke.length > 0 : Object.keys(smoke as object).length > 0) ? (
            <div>
              <Text size="xs" c="dimmed" mb={4}>smoke_attempts</Text>
              <ScrollArea.Autosize mah={160}>
                <Code block>{JSON.stringify(smoke, null, 2)}</Code>
              </ScrollArea.Autosize>
            </div>
          ) : null}
          <div>
            <Text size="xs" c="dimmed" mb={4}>payload</Text>
            <ScrollArea.Autosize mah={220}>
              <Code block>{JSON.stringify(payloadDigest(payload), null, 2)}</Code>
            </ScrollArea.Autosize>
          </div>
        </Stack>
      </Spoiler>
    </Stack>
  )
}

function SummaryRow({ k, v }: { k: string; v: string }) {
  return (
    <Group justify="space-between" wrap="nowrap" gap="md">
      <Text size="xs" c="dimmed">{k}</Text>
      <Text size="xs" ff="monospace" style={{ textAlign: 'right', wordBreak: 'break-all' }}>{v}</Text>
    </Group>
  )
}

export default JobDetailDrawer
